import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { getErrorMessage } from '@/lib/utils';
import { Shield, RefreshCw, Loader2, User, ChevronDown } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import { BACKEND_URL } from '@/lib/api';
const API = `${BACKEND_URL}/api`;

// Module groups for better organization
const MODULE_GROUP_CONFIG = {
  'Core': { color: 'bg-blue-100 text-blue-800', border: 'border-blue-200' },
  'Finance': { color: 'bg-green-100 text-green-800', border: 'border-green-200' },
  'Inventory': { color: 'bg-amber-100 text-amber-800', border: 'border-amber-200' },
  'Stock': { color: 'bg-orange-100 text-orange-800', border: 'border-orange-200' },
  'HR': { color: 'bg-purple-100 text-purple-800', border: 'border-purple-200' },
  'Reports': { color: 'bg-cyan-100 text-cyan-800', border: 'border-cyan-200' },
  'Admin': { color: 'bg-red-100 text-red-800', border: 'border-red-200' },
};

const UserAccess = () => {
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [groupedModules, setGroupedModules] = useState({});
  const [loading, setLoading] = useState(true);
  const [togglingModule, setTogglingModule] = useState(null);
  const [resetting, setResetting] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedUserId) {
      const user = users.find(u => u.user_id === selectedUserId);
      setSelectedUser(user);
    } else {
      setSelectedUser(null);
    }
  }, [selectedUserId, users]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [modulesRes, usersRes] = await Promise.all([
        axios.get(`${API}/user-access/modules`, { headers }),
        axios.get(`${API}/user-access/all`, { headers })
      ]);
      setGroupedModules(modulesRes.data.grouped);
      setUsers(usersRes.data);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to fetch data'));
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePermission = async (module) => {
    if (!selectedUserId) return;
    
    setTogglingModule(module);
    try {
      const response = await axios.post(
        `${API}/user-access/${selectedUserId}/toggle/${module}`, 
        {}, 
        { headers }
      );
      
      // Update local state
      setUsers(users.map(u => {
        if (u.user_id === selectedUserId) {
          return {
            ...u,
            permissions: {
              ...u.permissions,
              [module]: response.data.enabled
            },
            has_custom_permissions: true
          };
        }
        return u;
      }));
      
      toast.success(response.data.message);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update permission'));
    } finally {
      setTogglingModule(null);
    }
  };

  const handleResetPermissions = async () => {
    if (!selectedUserId || !selectedUser) return;
    
    if (!window.confirm(`Reset permissions for "${selectedUser.user_name}" to default ${selectedUser.role} role permissions?`)) {
      return;
    }
    
    setResetting(true);
    try {
      await axios.post(`${API}/user-access/${selectedUserId}/reset`, {}, { headers });
      toast.success(`Permissions reset for ${selectedUser.user_name}`);
      await fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to reset permissions'));
    } finally {
      setResetting(false);
    }
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      admin: 'bg-purple-100 text-purple-800',
      general_manager: 'bg-pink-100 text-pink-800',
      accountant: 'bg-indigo-100 text-indigo-800',
      cashier: 'bg-blue-100 text-blue-800',
      storekeeper: 'bg-green-100 text-green-800',
      bartender: 'bg-amber-100 text-amber-800',
      kitchen: 'bg-orange-100 text-orange-800',
      cafe: 'bg-cyan-100 text-cyan-800',
    };
    return colors[role] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
              <Shield className="w-5 h-5 text-blue-500" />
              User Access Control
            </CardTitle>
            <p className="text-sm text-gray-500 mt-1">
              Select a user and toggle module access permissions
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* User Selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium mb-2">Select User</label>
          <Select value={selectedUserId} onValueChange={setSelectedUserId}>
            <SelectTrigger className="w-full max-w-md" data-testid="user-select">
              <SelectValue placeholder="Choose a user to manage access..." />
            </SelectTrigger>
            <SelectContent>
              {users.map((user) => (
                <SelectItem key={user.user_id} value={user.user_id}>
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-gray-400" />
                    <span>{user.user_name}</span>
                    <span className="text-gray-400">({user.user_email})</span>
                    <Badge className={`text-xs ${getRoleBadgeColor(user.role)}`}>
                      {user.role}
                    </Badge>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Selected User Info & Permissions */}
        {selectedUser ? (
          <div className="space-y-6">
            {/* User Info Card */}
            <div className="p-4 bg-slate-50 rounded-lg flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <User className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">{selectedUser.user_name}</h3>
                  <p className="text-sm text-gray-500">{selectedUser.user_email}</p>
                  <Badge className={`mt-1 ${getRoleBadgeColor(selectedUser.role)}`}>
                    {selectedUser.role.toUpperCase()}
                  </Badge>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {selectedUser.has_custom_permissions && selectedUser.role !== 'admin' && (
                  <Badge variant="outline" className="text-amber-600 border-amber-300">
                    Custom Permissions
                  </Badge>
                )}
                {selectedUser.role !== 'admin' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleResetPermissions}
                    disabled={resetting}
                    data-testid="reset-permissions-btn"
                  >
                    {resetting ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4 mr-2" />
                    )}
                    Reset to Defaults
                  </Button>
                )}
              </div>
            </div>

            {/* Admin Notice */}
            {selectedUser.role === 'admin' && (
              <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg text-purple-800">
                <p className="font-medium">👑 Admin users have full access to all modules</p>
                <p className="text-sm mt-1">Admin permissions cannot be modified.</p>
              </div>
            )}

            {/* Module Permissions by Group */}
            {selectedUser.role !== 'admin' && (
              <div className="space-y-4">
                {Object.entries(groupedModules).map(([groupName, modules]) => (
                  <div 
                    key={groupName} 
                    className={`border rounded-lg overflow-hidden ${MODULE_GROUP_CONFIG[groupName]?.border || 'border-gray-200'}`}
                  >
                    {/* Group Header */}
                    <div className={`px-4 py-3 ${MODULE_GROUP_CONFIG[groupName]?.color || 'bg-gray-100'}`}>
                      <h4 className="font-semibold">{groupName}</h4>
                    </div>
                    
                    {/* Modules List */}
                    <div className="divide-y">
                      {modules.map((module) => {
                        const isEnabled = selectedUser.permissions[module.key] || false;
                        const isToggling = togglingModule === module.key;
                        
                        return (
                          <div 
                            key={module.key}
                            className={`flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors ${isEnabled ? 'bg-green-50/50' : ''}`}
                          >
                            <div className="flex items-center gap-3">
                              <span className="font-medium">{module.name}</span>
                            </div>
                            <div className="flex items-center gap-3">
                              {isToggling ? (
                                <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                              ) : (
                                <>
                                  <span className={`text-sm ${isEnabled ? 'text-green-600' : 'text-gray-400'}`}>
                                    {isEnabled ? 'Enabled' : 'Disabled'}
                                  </span>
                                  <Checkbox
                                    checked={isEnabled}
                                    onCheckedChange={() => handleTogglePermission(module.key)}
                                    className="data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600 h-5 w-5"
                                    data-testid={`access-${module.key}`}
                                  />
                                </>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          /* No User Selected State */
          <div className="text-center py-12 bg-slate-50 rounded-lg">
            <User className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-600">Select a User</h3>
            <p className="text-gray-500 mt-1">Choose a user from the dropdown above to manage their access permissions</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default UserAccess;
